import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/contexts/LocaleContext'
import { LANGUAGE_INTL_LOCALE } from '@/i18n/types'
import type { Plan, PlanPackage } from '@/services/billingService'
import { formatAmount } from './price'

const LEAD_IN_PREFIX = 'everything_in_'

/**
 * The one plan worth suggesting, and what it adds.
 *
 * Only the capabilities the plan brings on top of the one below it are shown —
 * the catalogue already lists them that way after its "everything in …" lead-
 * in — so the card answers "what would I get" rather than repeating what the
 * account already has. The list comes from `plans.py`; nothing here decides
 * which features belong to which plan.
 *
 * One action, decided from the payload: a plan sold by conversation opens the
 * mail, an announced plan says "coming soon", anything else buys.
 */
export default function NextStepCard({
  plan,
  ownedPlanName,
  onBuy,
  onCompare,
  busy,
  error,
  contactHref,
}: {
  plan: Plan
  /** The plan the account is on, if it bought one — "everything in X stays". */
  ownedPlanName: string | null
  onBuy: (pkg: PlanPackage) => void
  onCompare: () => void
  busy: boolean
  error?: string
  contactHref: string
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')
  const { language } = useLocale()

  const pkg = plan.packages.find((candidate) => candidate.recommended) ?? plan.packages[0] ?? null
  const gains = plan.includes.filter(
    (key) => !key.startsWith(LEAD_IN_PREFIX) && t(`includes.${key}`, { defaultValue: '' }),
  )
  const name = t(`plan.${plan.id}.name`, { defaultValue: plan.id })
  const comingSoon = plan.on_sale === false
  const respondents = pkg
    ? t('packages.perMonth', {
        count: pkg.respondents,
        formatted: pkg.respondents.toLocaleString(LANGUAGE_INTL_LOCALE[language]),
      })
    : null

  return (
    <Box
      sx={{
        bgcolor: c.white,
        borderRadius: '20px',
        p: { xs: 2.5, md: 4 },
        border: `1.5px solid ${alpha(c.coral, 0.18)}`,
        boxShadow: '0 1px 2px rgba(27,28,56,.04), 0 8px 24px rgba(27,28,56,.05)',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              mb: 1.25,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: alpha(c.coral, 0.1),
              color: c.coral,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
            }}
          >
            {comingSoon ? t('badge.comingSoon') : t('badge.recommended')}
          </Box>
          <Typography
            component="h3"
            sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 26, fontWeight: 700, letterSpacing: '-0.015em' }}
          >
            {name}
          </Typography>
          <Typography sx={{ mt: 0.5, color: c.ink56 }}>
            {[respondents, t(`plan.${plan.id}.tagline`, { defaultValue: '' })].filter(Boolean).join(' · ')}
          </Typography>
          {ownedPlanName && (
            <Typography sx={{ mt: 0.5, fontSize: 14, color: c.ink56 }}>
              {t('next.keepsAll', { plan: ownedPlanName })}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: { sm: 'right' }, flexShrink: 0 }}>
          {pkg ? (
            <>
              <Typography
                sx={{
                  fontFamily: theme.affexy.fonts.heading,
                  fontSize: 30,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}
              >
                {t('next.price', { amount: formatAmount(pkg.price_per_month_pln) })}
              </Typography>
              <Typography sx={{ fontSize: 13, color: c.ink56 }}>{t('next.perMonthNet')}</Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 22, fontWeight: 700 }}>
                {t('next.quoted')}
              </Typography>
              <Typography sx={{ fontSize: 13, color: c.ink56 }}>{t('price.customHint')}</Typography>
            </>
          )}
        </Box>
      </Stack>

      {gains.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 2.5, p: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {gains.map((key) => (
            <Box
              component="li"
              key={key}
              sx={{ bgcolor: c.gray50, borderRadius: 999, px: 1.5, py: 0.75, fontSize: 14, lineHeight: 1.35 }}
            >
              {t(`includes.${key}`)}
            </Box>
          ))}
        </Box>
      )}

      <Stack direction="row" sx={{ mt: 3, gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {!plan.self_serve ? (
          <Button variant="contained" href={contactHref || undefined} disabled={!contactHref} sx={ACTION_SX}>
            {t('actions.contact')}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={comingSoon || busy || !pkg}
            onClick={() => pkg && onBuy(pkg)}
            sx={ACTION_SX}
          >
            {busy ? (
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
            ) : comingSoon ? (
              t('actions.comingSoon')
            ) : ownedPlanName ? (
              t('actions.upgrade', { plan: name })
            ) : (
              t('actions.buy')
            )}
          </Button>
        )}
        <Button
          onClick={onCompare}
          sx={{ ...ACTION_SX, bgcolor: c.gray100, color: c.ink, '&:hover': { bgcolor: c.gray200 } }}
        >
          {t('next.compare')}
        </Button>
      </Stack>

      {error && <Typography sx={{ mt: 1.5, fontSize: 13.5, color: c.coral }}>{error}</Typography>}
    </Box>
  )
}

const ACTION_SX = {
  height: 44,
  px: 2.75,
  borderRadius: '12px',
  fontSize: 15,
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
  '&:active': { transform: 'scale(0.97)' },
  transition: 'transform 100ms ease-out, background-color 150ms',
} as const
